import React, { useEffect, useState } from 'react';
import { Card, Modal } from 'react-bootstrap';
import moment from 'moment';
import { Cake, Calendar, CalendarDays } from 'lucide-react';
import { makeRESTApiRequest } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';

const BIRTH_DATE_FORMAT = 'YYYY-MM-DD';

const parseBirthDate = date => {
  const calendar = moment(date, BIRTH_DATE_FORMAT, true);
  if (calendar.isValid()) return calendar;
  // Legacy ISO instants (Eastern midnight stored as UTC)
  const legacy = moment.utc(date);
  return legacy.isValid() ? legacy : moment.invalid();
};

const formatBirthDateShort = date => {
  const m = parseBirthDate(date);
  return m.isValid() ? m.format('MMM D') : '—';
};

const formatRangeShort = (start, end) => {
  const s = moment(start);
  const e = moment(end);
  if (!s.isValid()) return '—';
  if (!e.isValid() || s.isSame(e, 'day')) return s.format('MMM D');
  return `${s.format('MMM D')} - ${e.format('MMM D')}`;
};

const upcomingFilter = row => {
  const today = moment().startOf('day');
  const monthStart = moment().startOf('month');
  const monthEnd = moment().endOf('month');

  if (row.startDate != null || row.endDate != null) {
    const rangeStart = moment(row.startDate);
    const rangeEnd = moment(row.endDate ?? row.startDate);
    if (!rangeStart.isValid() || !rangeEnd.isValid()) return false;
    const overlapsThisMonth =
      rangeStart.isSameOrBefore(monthEnd, 'day') && rangeEnd.isSameOrAfter(monthStart, 'day');
    if (!overlapsThisMonth) return false;
    return rangeEnd.isSameOrAfter(today, 'day');
  }
  if (row.date != null) {
    const birth = parseBirthDate(row.date);
    if (!birth.isValid()) return false;
    const occurrence = moment([today.year(), birth.month(), birth.date()]);
    if (!occurrence.isValid()) return false;
    if (!occurrence.isSame(today, 'month')) return false;
    return occurrence.isSameOrAfter(today, 'day');
  }
  return false;
};

function TeamManagementStatsSkeleton() {
  const skeletonCard = borderColor => (
    <Card className="p-4 w-100 border-none" style={{ borderLeft: `4px solid ${borderColor}` }}>
      <Card.Title className="mb-3">
        <div className="d-flex align-items-center">
          <div className="shimmer rounded mr-2 flex-shrink-0" style={{ width: 21, height: 21 }} aria-hidden />
          <div className="shimmer rounded flex-grow-1" style={{ maxWidth: 220, height: 21 }} aria-hidden />
        </div>
      </Card.Title>
      <Card.Body className="p-0">
        <ul className="list-unstyled mb-0 midFont">
          {[0, 1].map(i => (
            <li
              key={i}
              className="d-flex justify-content-between align-items-baseline border-bottom border-white gap-2 py-1"
            >
              <div className="shimmer rounded flex-grow-1 me-2" style={{ maxWidth: '58%', height: 17}} aria-hidden />
              <div className="shimmer rounded flex-shrink-0" style={{ width: 52, height: 17}} aria-hidden />
            </li>
          ))}
        </ul>
        <div className="d-flex justify-content-end mt-2">
          <div className="shimmer rounded flex-shrink-0" style={{ width: 52, height: 17}} aria-hidden />
        </div>
      </Card.Body>
    </Card>
  );

  return (
    <div
      className="d-flex flex-column flex-md-row align-items-stretch justify-content-between mb-4 w-100"
      style={{ gap: '1rem' }}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <span className="sr-only">Loading team stats</span>
      {skeletonCard('#7c3aed')}
      {skeletonCard('#2563eb')}
      {skeletonCard('#d97706')}
    </div>
  );
}

export default function TeamManagementStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showViewAllStatsModal, setShowViewAllStatsModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      const { response, error: err } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.TEAM_MANAGEMENT_STATS,
        method: 'GET'
      });
      if (cancelled) return;
      if (err) {
        setError(err);
        setData(null);
      } else {
        setData(response);
      }
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <TeamManagementStatsSkeleton />;
  }

  const teamStatsCards = [
    {
      id: 'birthdays',
      borderColor: '#a855f7',
      backgroundColor: '#FAF5FF4D',
      Icon: Cake,
      title: 'Birthdays This Month',
      emptyMessage: error ? 'No birthday data available' : 'No birthdays this month',
      rows: data?.birthdays || [],
      rowFilter: upcomingFilter,
      dateText: row => formatBirthDateShort(row.date),
      viewAllLabel: `Birthdays`,
      modalColumns: { first: 'Name', second: 'Date' }
    },
    {
      id: 'teamOff',
      borderColor: '#3b82f6',
      backgroundColor: '#EFF6FF4D',
      Icon: CalendarDays,
      title: 'Team Members OFF',
      emptyMessage: error ? 'No time off data available' : 'No time off scheduled this month',
      rows: data?.teamMembersOffThisYear || [],
      dateText: row => formatRangeShort(row.startDate, row.endDate),
      rowFilter: upcomingFilter,
      viewAllLabel: `Time OFFs`,
      modalColumns: { first: 'Name', second: 'Date' }
    },
    {
      id: 'holidays',
      borderColor: '#f59e0b',
      backgroundColor: '#FFFBEB4D',
      Icon: Calendar,
      title: 'Holidays This Month',
      emptyMessage: error ? 'No holiday data available' : 'No holidays this month.',
      rows: data?.holidaysThisYear || [],
      rowFilter: upcomingFilter,
      dateText: row => formatRangeShort(row.startDate, row.endDate),
      viewAllLabel: `${moment().year()} Holidays`,
      modalColumns: { first: 'Holiday', second: 'Date' }
    }
  ];

  return (
    <>
      <div className="d-flex flex-column flex-md-row align-items-stretch justify-content-between mb-4 w-100" style={{ gap: '1rem' }}>
        {teamStatsCards.map(card => {
          const {
            Icon,
            borderColor,
            backgroundColor,
            title,
            emptyMessage,
            rows,
            dateText,
            viewAllLabel,
            rowFilter
          } = card;
          const displayRows = rowFilter ? rows.filter(rowFilter) : rows;
          return (
            <Card
              key={card.id}
              className="p-4 w-100 border-none"
              style={{ borderLeft: `4px solid ${borderColor}`, backgroundColor: backgroundColor }}
            >
              <Card.Title>
                <div className="d-flex align-items-center">
                  <Icon size={18} style={{ color: borderColor }} className="mr-2" aria-hidden />
                  <span className="fw-bold text-dark xlargeFont">{title}</span>
                </div>
              </Card.Title>
              <Card.Body className="p-0 mt-1">
                {displayRows.length === 0 ? (
                  <p className={`text-muted small mb-0 fst-italic`}>{emptyMessage}</p>
                ) : (
                  <ul className="list-unstyled mb-0 flex-grow-1 midFont">
                    {displayRows.slice(0, 2).map((row, idx) => (
                      <li
                        key={`${row.name}-${idx}`}
                        className={`d-flex justify-content-between align-items-center mb-1`}
                      >
                        <span className={`fw-semibold text-dark text-truncate me-2 midFont`}>{row.name || '—'}</span>
                        <span className="text-muted text-nowrap flex-shrink-0 midFont">{dateText(row)}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="d-flex justify-content-end mt-2">
                  <button
                    className=" text-muted hover-text-dark border-0 p-0 m-0 bg-transparent underline"
                    onClick={() => {
                      setSelectedCard(card);
                      setShowViewAllStatsModal(true);
                    }}
                  >
                    <span className="xsmallFont">{viewAllLabel}</span>
                  </button>
                </div>
              </Card.Body>
            </Card>
          );
        })}
      </div>
      <ViewAllStatsModal
        show={showViewAllStatsModal}
        onHide={() => {
          setShowViewAllStatsModal(false);
        }}
        onExited={() => setSelectedCard(null)}
        statData={selectedCard}
      />
    </>
  );
}

const ViewAllStatsModal = ({ show, onHide, onExited, statData }) => {
  const isStatsRowPast = row => {
    if (row.date != null && row.startDate == null && row.endDate == null) {
      const birth = parseBirthDate(row.date);
      if (!birth.isValid()) return false;
      const occurrence = moment([moment().year(), birth.month(), birth.date()]).endOf('day');
      return occurrence.isBefore(moment().startOf('day'));
    }
    const ref = row.endDate ?? row.startDate;
    if (!ref) return false;
    const d = moment(ref);
    if (!d.isValid()) return false;
    return d.endOf('day').isBefore(moment().startOf('day'));
  };

  const rows = statData?.rows ?? [];
  const nameHeader = statData?.modalColumns?.first ?? 'Name';
  const dateHeader = statData?.modalColumns?.second ?? 'Date';

  return (
    <Modal
      show={show}
      onHide={onHide}
      onExited={onExited}
      centered
      backdrop="static"
      dialogClassName="team-management-stats-dialog"
      contentClassName="team-management-stats-content"
>
      <Modal.Header closeButton>
        <Modal.Title>{statData?.viewAllLabel}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflowY: 'auto' }}>
      {rows.length === 0 ? (
          <p className="text-muted small mb-0 fst-italic">{statData?.emptyMessage ?? 'No rows to show.'}</p>
        ) : (
            <ul className="list-unstyled mb-0 midFont">
            <li className="d-flex justify-content-between align-items-center py-2 border-bottom font-weight-bold largeFont">
              <span className="text-truncate me-2">{nameHeader}</span>
              <span className="text-nowrap flex-shrink-0">{dateHeader}</span>
            </li>
            {rows.map((row, idx) => (
              <li
                key={`${row.name}-${idx}`}
                className={`d-flex justify-content-between align-items-center py-2${
                  idx < rows.length - 1 ? ' border-bottom' : ''
                }`}
                style={{ gap: 16 }}
              >
                <span
                  className={`fw-semibold text-dark text-truncate me-2 midFont ${
                    isStatsRowPast(row) ? 'text-muted' : ''
                  }`}
                >
                  {row.name || '—'}
                </span>
                <span className={`text-nowrap flex-shrink-0 midFont ${isStatsRowPast(row) ? 'text-muted' : ''}`}>
                  {statData?.dateText ? statData?.dateText(row) : '—'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Modal.Body>
    </Modal>
  );
};
